import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../lib/api';
import { pathFor, type HitType } from './paths';

/**
 * Globale Suche (design.md §5): erkennt UUID, Discord-ID, `ns:item`, Join-ID oder Name.
 * Enter springt bei eindeutigem Treffer direkt zur Detailseite, sonst zum ersten Treffer der Liste.
 * Tastenkürzel `/`, Pfeiltasten in der Liste, Esc schließt.
 */

interface Hit {
  type: HitType;
  id: string;
  label: string;
  sub: string | null;
}
interface Result {
  exact: { type: HitType; id: string } | null;
  hits: Hit[];
}

const GROUP: Record<HitType, string> = { user: 'Spieler', team: 'Team', item: 'Item', chest: 'Kiste' };

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const term = useDebounced(q.trim(), 200);

  const result = useQuery({
    queryKey: ['admin-search', term],
    queryFn: () => api.get<Result>('/admin/search', { q: term }),
    enabled: term.length >= 2,
    staleTime: 10_000,
  });
  const hits = term.length >= 2 ? (result.data?.hits ?? []) : [];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) && !target.isContentEditable) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => setActive(0), [term]);

  const go = (type: HitType, id: string) => {
    setOpen(false);
    setQ('');
    inputRef.current?.blur();
    navigate(pathFor(type, id));
  };

  const onKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      if (hits.length) setActive((a) => (a + (e.key === 'ArrowDown' ? 1 : hits.length - 1)) % hits.length);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const current = q.trim();
      if (current.length < 2) return;
      // Nicht auf die Entprellung warten: Enter fragt sofort.
      const r = current === term && result.data ? result.data : await api.get<Result>('/admin/search', { q: current });
      if (r.exact) return go(r.exact.type, r.exact.id);
      const pick = current === term ? hits[active] : r.hits[0];
      if (pick) go(pick.type, pick.id);
      else setOpen(true);
    }
  };

  const showList = open && term.length >= 2;

  return (
    <div className="gsearch">
      <Search aria-hidden />
      <input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-label="Suche: Spieler, UUID, Item, Team, Join-ID"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={showList && hits[active] ? `${listId}-${active}` : undefined}
        placeholder="Suche: Spieler, UUID, Item, Team, Join-ID …"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
      />
      <kbd aria-hidden>/</kbd>
      {showList && (
        <ul className="gsearch-list" id={listId} role="listbox">
          {result.isFetching && hits.length === 0 && <li className="muted">Sucht…</li>}
          {!result.isFetching && hits.length === 0 && <li className="muted">Keine Treffer.</li>}
          {hits.map((h, i) => (
            <li
              key={`${h.type}:${h.id}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => go(h.type, h.id)}
              onMouseEnter={() => setActive(i)}
            >
              <span className="gs-type">{GROUP[h.type]}</span>
              <span className={h.type === 'item' ? 'mono' : undefined}>{h.label}</span>
              {h.sub && <span className="muted">{h.sub}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
